import { ButtonLink, PrimaryButton } from '@designsforhealth/dfh-react-components'
import React, { FC, useCallback, useState } from 'react'
import styled from 'styled-components'
import Modal from 'styled-react-modal'

import Filters, { FiltersProps } from './Filters'

import filterIcon from '../../../../img/filter.svg'

export interface MobileFiltersProps extends FiltersProps {
  className?: string
  loading: boolean
  activeFilters: string[]
}

const StyledModal = Modal.styled(({ theme }) => ({
  backgroundColor: theme.colors.grayscale.white,
  height: '100%',
  flex: '1',
  overflowY: 'scroll',
  marginLeft: 'auto',
  maxWidth: '475px',
  minHeight: '100%',
  padding: '10px',
  paddingBottom: '90px',
}))

const StyledOpenMobileFilters = styled(ButtonLink)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.grayscale.white,
  display: 'flex',
  fontFamily: theme.fontFamilies.robotoCondensed,
  fontSize: '14px',
  height: '60px',
  lineHeight: '16px',
  paddingLeft: '15px',
  paddingRight: '15px',
  textDecoration: 'none',
  width: '100%',
  boxSizing: 'border-box',
  img: {
    height: '1em',
    marginLeft: '8px',
  },
}))

const StyledApplyFiltersContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  padding: 24px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.grayscale.white};

  @media (min-width: 475px) {
    display: none;
  }
`

const StyledApplyFilters = styled(PrimaryButton)`
  width: 100%;
`

const StyledTopContainer = styled.div`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool450};
  align-items: center;
  justify-content: space-between;
  height: 60px;
  width: 100%;
  font-family: ${({ theme }) => theme.fontFamilies.robotoCondensed};
  font-size: 14px;
  margin-bottom: 20px;
  margin-left: -10px;
  margin-right: -10px;
  padding-left: 10px;
  padding-right: 10px;
`

const StyledCloseMobileFilters = styled(ButtonLink)(({ theme }) => ({
  textDecoration: 'none',
  fontFamily: theme.fontFamilies.robotoCondensed,
}))

const StyledClearAllFilters = styled(ButtonLink)(({ theme }) => ({
  textDecoration: 'underline',
  fontFamily: theme.fontFamilies.robotoCondensed,
}))

const MobileFilters: FC<MobileFiltersProps> = ({
  className,
  aggregations,
  listing,
  loading,
  activeFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { defaultState, apply, nextState } = listing

  const hasFilters =
    !!aggregations &&
    Object.values(aggregations).some(
      (aggregation) =>
        !!aggregation && typeof aggregation === 'object' && aggregation?.options.length > 0,
    )

  const handleClearAllFilters = useCallback(
    () => apply(nextState.withFilters(defaultState.filters)),
    [apply, defaultState.filters, nextState],
  )

  const filtersApplied = activeFilters?.length > 0
  const toggleModal = () => setIsOpen((prev) => !prev)

  return (
    <div className={className}>
      <StyledOpenMobileFilters onClick={hasFilters ? toggleModal : undefined}>
        FILTER
        <img alt="Open product filters modal" src={filterIcon} />
      </StyledOpenMobileFilters>
      <StyledModal
        allowScroll={false}
        isOpen={isOpen}
        onBackgroundClick={toggleModal}
        onEscapeKeydown={toggleModal}
      >
        <StyledTopContainer>
          <StyledCloseMobileFilters onClick={toggleModal}>&#10094; BACK</StyledCloseMobileFilters>
          {filtersApplied && (
            <StyledClearAllFilters onClick={handleClearAllFilters}>CLEAR ALL</StyledClearAllFilters>
          )}
        </StyledTopContainer>
        <Filters aggregations={aggregations} listing={listing} />
        <StyledApplyFiltersContainer>
          <StyledApplyFilters onClick={toggleModal} disabled={loading}>
            Accept
          </StyledApplyFilters>
        </StyledApplyFiltersContainer>
      </StyledModal>
    </div>
  )
}

MobileFilters.defaultProps = {
  className: '',
  aggregations: null,
}

const StyledMobileFilters = styled(MobileFilters)(({ theme }) => ({
  flex: '1 50%',
  minWidth: 'fit-content',
  order: -1,
  '.open-mobile-filters': {
    alignItems: 'center',
    backgroundColor: theme.colors.grayscale.white,
    display: 'flex',
    fontFamily: theme.fontFamilies.robotoCondensed,
    fontSize: '14px',
    height: '60px',
    lineHeight: '16px',
    paddingLeft: '15px',
    paddingRight: '15px',
    textDecoration: 'none',
    boxSizing: 'border-box',
    img: {
      marginLeft: '8px',
    },
  },
}))

export default StyledMobileFilters
